/**
 * PlansHome — /#/plans
 *
 * Plans approval queue / plans home / weekly editor.
 * Roles: BDR, AE, Manager
 *
 * Tabs: My Plans / Team Plans / Templates
 * Seed data inline — marked with "seed data" pill.
 */

import { useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const BG = "#FAFAF7";

// ── Seed data ────────────────────────────────────────────────────────────────

type PlanStatus = "draft" | "submitted" | "approved" | "rejected";

interface Plan {
  id: string;
  title: string;
  dateRange: string;
  status: PlanStatus;
  pctComplete: number;
  owner: string;
  role: string;
}

const MY_PLANS: Plan[] = [
  { id: "p1", title: "Week of Jun 9 — Steel / Distribution push", dateRange: "Jun 9–13, 2025", status: "approved", pctComplete: 100, owner: "You", role: "AE" },
  { id: "p2", title: "Week of Jun 16 — Automotive OEM outreach", dateRange: "Jun 16–20, 2025", status: "submitted", pctComplete: 78, owner: "You", role: "AE" },
  { id: "p3", title: "Week of Jun 23 — Medical Device follow-up", dateRange: "Jun 23–27, 2025", status: "draft", pctComplete: 34, owner: "You", role: "AE" },
];

const TEAM_PLANS: Plan[] = [
  { id: "t1", title: "Week of Jun 16 — BDR discovery blitz", dateRange: "Jun 16–20, 2025", status: "approved", pctComplete: 92, owner: "Jordan M.", role: "BDR" },
  { id: "t2", title: "Week of Jun 16 — IoT vertical sprint", dateRange: "Jun 16–20, 2025", status: "submitted", pctComplete: 65, owner: "Alex R.", role: "AE" },
  { id: "t3", title: "Week of Jun 16 — Aerospace deepens", dateRange: "Jun 16–20, 2025", status: "submitted", pctComplete: 71, owner: "Casey L.", role: "AE" },
  { id: "t4", title: "Week of Jun 9 — Building materials reactivation", dateRange: "Jun 9–13, 2025", status: "approved", pctComplete: 100, owner: "Morgan K.", role: "BDR" },
  { id: "t5", title: "Week of Jun 23 — Mfg expansion", dateRange: "Jun 23–27, 2025", status: "draft", pctComplete: 12, owner: "Riley P.", role: "AE" },
];

interface Template {
  id: string;
  name: string;
  description: string;
  vertical: string;
  tasks: number;
}

const TEMPLATES: Template[] = [
  { id: "tmpl1", name: "Standard AE Weekly", description: "Discovery calls, pipeline reviews, proposal follow-ups, next-step confirmations.", vertical: "All verticals", tasks: 8 },
  { id: "tmpl2", name: "BDR Discovery Sprint", description: "Cold outreach, CP1 qualification, booking rate targets, cadence steps.", vertical: "All verticals", tasks: 6 },
  { id: "tmpl3", name: "Automotive OEM Push", description: "Tailored for Tier 1 and Tier 2 automotive suppliers. M-PACT aligned milestones.", vertical: "Automotive", tasks: 10 },
  { id: "tmpl4", name: "Medical Device Compliance Play", description: "Quality system angle, compliance officer targeting, contract cycle awareness.", vertical: "Medical device", tasks: 9 },
  { id: "tmpl5", name: "IoT / Connected Systems", description: "Technical buyer discovery, proof-of-concept framing, security objection handling.", vertical: "IoT", tasks: 7 },
  { id: "tmpl6", name: "Building Materials — Q3 Push", description: "Seasonal demand capture, contractor network plays, SKU rationalization angle.", vertical: "Building materials", tasks: 8 },
];

// ── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: PlanStatus }) {
  const map: Record<PlanStatus, { bg: string; color: string; label: string }> = {
    draft:     { bg: "#EFEFEE", color: "#525258", label: "Draft" },
    submitted: { bg: "#FAFAF7", color: "#0B0B0F", label: "Submitted" },
    approved:  { bg: "#F0FDF4", color: "#15803D", label: "Approved" },
    rejected:  { bg: "#FEF2F2", color: "#B91C1C", label: "Rejected" },
  };
  const s = map[status];
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        padding: "3px 8px",
        borderRadius: 99,
      }}
    >
      {s.label}
    </span>
  );
}

// ── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          flex: 1,
          height: 5,
          borderRadius: 99,
          background: "#E5E5E7",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: pct === 100 ? "#16A34A" : BLACK,
            borderRadius: 99,
          }}
        />
      </div>
      <span style={{ fontSize: 11, color: "#6B6B70", fontVariantNumeric: "tabular-nums", minWidth: 28, textAlign: "right" }}>
        {pct}%
      </span>
    </div>
  );
}

// ── Plan row ─────────────────────────────────────────────────────────────────

function PlanRow({ plan }: { plan: Plan }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 140px 110px 140px 80px",
        alignItems: "center",
        gap: 16,
        padding: "14px 20px",
        borderBottom: "1px solid #EFEFEE",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#FAFAF7"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: BLACK }}>{plan.title}</div>
        <div style={{ fontSize: 11, color: "#8E8E94", marginTop: 2 }}>{plan.dateRange}</div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: "#6B6B70" }}>{plan.owner}</div>
        <div style={{ fontSize: 10, color: "#8E8E94", letterSpacing: "0.06em", textTransform: "uppercase" }}>{plan.role}</div>
      </div>
      <StatusBadge status={plan.status} />
      <ProgressBar pct={plan.pctComplete} />
      <button
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          padding: "5px 12px",
          borderRadius: 6,
          background: "transparent",
          border: `1px solid ${BLACK}`,
          color: BLACK,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        Open
      </button>
    </div>
  );
}

// ── Template card ─────────────────────────────────────────────────────────────

function TemplateCard({ tmpl }: { tmpl: Template }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        cursor: "pointer",
        transition: "box-shadow 0.13s, border-color 0.13s",
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.boxShadow = "0 4px 12px rgba(15,23,42,0.1)";
        el.style.borderColor = "#C9C9CC";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.boxShadow = "none";
        el.style.borderColor = "#E5E5E7";
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: BLACK }}>{tmpl.name}</div>
        <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 4, lineHeight: 1.5 }}>{tmpl.description}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
        <span style={{ fontSize: 10, color: "#8E8E94", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {tmpl.vertical} · {tmpl.tasks} tasks
        </span>
        <button
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            padding: "5px 12px",
            borderRadius: 6,
            background: BLACK,
            color: "#FFFFFF",
            border: "none",
            cursor: "pointer",
          }}
        >
          Use template
        </button>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

type Tab = "my" | "team" | "templates";

export default function PlansHome() {
  const [activeTab, setActiveTab] = useState<Tab>("my");

  const tabs: { id: Tab; label: string }[] = [
    { id: "my", label: "My Plans" },
    { id: "team", label: "Team Plans" },
    { id: "templates", label: "Templates" },
  ];

  return (
    <OperatorShell breadcrumbs={[{ label: "Plans" }]}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Page header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <h1 style={{ fontSize: 26, fontWeight: 900, color: BLACK, margin: 0, letterSpacing: "-0.01em" }}>
                Plans
              </h1>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  padding: "2px 7px",
                  borderRadius: 99,
                  background: "#FEF3C7",
                  color: "#92400E",
                  border: "1px solid #FDE68A",
                }}
              >
                Seed data
              </span>
            </div>
            <div style={{ fontSize: 14, color: "#6B6B70" }}>
              Weekly plans — draft, submit, approve, execute.
            </div>
          </div>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              background: RED,
              color: "#FFFFFF",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              border: "none",
              cursor: "pointer",
            }}
          >
            + New weekly plan
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: 4,
            marginBottom: 24,
            borderBottom: "1px solid #E5E5E7",
            paddingBottom: 0,
          }}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: "8px 16px",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                borderBottom: activeTab === t.id ? `2px solid ${BLACK}` : "2px solid transparent",
                color: activeTab === t.id ? BLACK : "#6B6B70",
                transition: "color 0.13s",
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab: My Plans */}
        {activeTab === "my" && (
          <div>
            {MY_PLANS.length === 0 ? (
              <div
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E5E5E7",
                  borderRadius: 12,
                  padding: "60px 24px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 600, color: BLACK, marginBottom: 8 }}>
                  No plans yet. Start with a template.
                </div>
                <div style={{ fontSize: 13, color: "#6B6B70", marginBottom: 20 }}>
                  Templates give you a proven weekly structure for your vertical and role.
                </div>
                <button
                  onClick={() => setActiveTab("templates")}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 8,
                    background: BLACK,
                    color: "#FFFFFF",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Browse templates
                </button>
              </div>
            ) : (
              <div
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E5E5E7",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                {/* Table header */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 140px 110px 140px 80px",
                    gap: 16,
                    padding: "10px 20px",
                    borderBottom: "1px solid #E5E5E7",
                    background: "#FAFAF7",
                  }}
                >
                  {["Plan", "Owner", "Status", "Progress", ""].map((h) => (
                    <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8E8E94" }}>
                      {h}
                    </div>
                  ))}
                </div>
                {MY_PLANS.map((p) => <PlanRow key={p.id} plan={p} />)}
              </div>
            )}
          </div>
        )}

        {/* Tab: Team Plans */}
        {activeTab === "team" && (
          <div>
            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5E5E7",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 140px 110px 140px 80px",
                  gap: 16,
                  padding: "10px 20px",
                  borderBottom: "1px solid #E5E5E7",
                  background: "#FAFAF7",
                }}
              >
                {["Plan", "Owner", "Status", "Progress", ""].map((h) => (
                  <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8E8E94" }}>
                    {h}
                  </div>
                ))}
              </div>
              {TEAM_PLANS.map((p) => <PlanRow key={p.id} plan={p} />)}
            </div>
          </div>
        )}

        {/* Tab: Templates */}
        {activeTab === "templates" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 16,
            }}
          >
            {TEMPLATES.map((t) => <TemplateCard key={t.id} tmpl={t} />)}
          </div>
        )}
      </div>
    </OperatorShell>
  );
}
